/**
 * XdcMonitor Page
 *
 * Monitor XDC Network accounts, masternodes, XRC20 tokens, and transactions.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useXdcWallets, useXdcPrice } from '../hooks/useXdcWallet';
import { useXdcMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidXdcAddress, xdcAPI, toXdcFormat } from '../services/xdcApi';
import { useToast } from '../components/Toast';
import { XdcHealthMetrics } from '../components/xdc/XdcHealthMetrics';
import { XdcSupplyBreakdown } from '../components/xdc/XdcSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { XDCWalletData } from '../types/xdc.types';

const XDC_COLORS = {
  primary: '#1D6FA3',
  secondary: '#2196F3',
  accent: '#00BCD4',
  gradient: 'linear-gradient(135deg, #1D6FA3, #2196F3)',
  lightGradient: 'linear-gradient(135deg, rgba(29, 111, 163, 0.15), rgba(33, 150, 243, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
};

type TabType = 'overview' | 'transactions' | 'tokens';

const s = {
  page: { padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '20px', flexWrap: 'wrap' as const },
  title: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleText: { fontSize: '24px', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '13px', color: 'var(--muted)', marginTop: '4px' },
  card: { background: 'rgba(0, 0, 0, 0.25)', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '20px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (color: string) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px', textAlign: 'center' as const }),
  statValue: { fontSize: '22px', fontWeight: 700 },
  statLabel: { fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace', outline: 'none' },
  button: { padding: '12px 20px', background: XDC_COLORS.gradient, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
  buttonSecondary: { padding: '12px 20px', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabContainer: { display: 'flex', gap: '4px', padding: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '14px', width: 'fit-content', marginBottom: '24px' },
  tab: (isActive: boolean) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease', background: isActive ? XDC_COLORS.gradient : 'transparent', color: isActive ? 'white' : 'var(--muted)' }),
  emptyState: { textAlign: 'center' as const, padding: '60px 20px' },
};

function NetworkStatsBanner({ xdcPrice, totalAccounts, isLoading }: { xdcPrice: number; totalAccounts: number; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: XDC_COLORS.lightGradient, border: '1px solid rgba(29, 111, 163, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>{t('xdc.network.title', 'XDC Network')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, color: XDC_COLORS.primary }}>${xdcPrice.toFixed(4)}</span>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>XDC</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalAccounts}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('xdc.network.accounts', 'Accounts')}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isLoading ? XDC_COLORS.warning : XDC_COLORS.success, boxShadow: `0 0 10px ${isLoading ? XDC_COLORS.warning : XDC_COLORS.success}`, margin: '0 auto 4px' }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{isLoading ? 'Syncing...' : 'Connected'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsOverview({ totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalPendingRewards, totalTokens, masternodeCount }: any) {
  const { t } = useTranslation();
  const stats = [
    { label: t('xdc.stats.balance', 'Total Balance'), value: `${totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })} XDC`, subvalue: `$${totalBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: XDC_COLORS.primary, icon: <Icons.Wallet size={16} /> },
    { label: t('xdc.stats.staked', 'Staked'), value: `${totalStaked.toLocaleString(undefined, { maximumFractionDigits: 2 })} XDC`, subvalue: `$${totalStakedUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: XDC_COLORS.accent, icon: <Icons.Lock size={16} /> },
    { label: t('xdc.stats.tokens', 'XRC20 Tokens'), value: totalTokens.toString(), subvalue: `${masternodeCount} masternodes`, color: XDC_COLORS.success, icon: <Icons.Coins size={16} /> },
  ];
  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function AccountCard({ wallet, onRemove }: { wallet: XDCWalletData; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: 'rgba(29, 111, 163, 0.08)', border: '1px solid rgba(29, 111, 163, 0.2)', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: XDC_COLORS.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Xdc size={20} color="white" />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{wallet.address.slice(0, 10)}...{wallet.address.slice(-6)}</div>
            {wallet.isMasternode && <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: 'rgba(16, 185, 129, 0.15)', color: XDC_COLORS.success, borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}><Icons.Server size={10} /> Masternode</div>}
          </div>
        </div>
        <button onClick={onRemove} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}><Icons.Trash2 size={12} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>Balance</div>
          <div style={{ fontSize: '18px', fontWeight: 600 }}>{wallet.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} XDC</div>
          <div style={{ fontSize: '11px', color: XDC_COLORS.success }}>${wallet.balanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>Staked</div>
          <div style={{ fontSize: '18px', fontWeight: 600, color: XDC_COLORS.accent }}>{wallet.stakedAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })} XDC</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>Tokens</div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{wallet.tokens.length} XRC20</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Xdc size={40} color="white" />}
      title={t('xdc.empty.title', 'Welcome to XDC Monitor')}
      description={t(
        'xdc.empty.description',
        'Track your XDC addresses, monitor masternode status, view XRC20 token holdings, and analyze transactions.',
      )}
      colors={XDC_COLORS}
    />
  );
}

export function XdcMonitor() {
  const { t } = useTranslation();
  const toast = useToast();
  const { wallets: accounts, setWallets: setAccounts } = useXdcMonitorWallets();
  const [newAccountInput, setNewAccountInput] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { wallets, isLoading, error, refetch, xdcPrice, totalBalance, totalBalanceUsd, totalStaked, totalStakedUsd, totalPendingRewards, totalTokens, masternodeCount } = useXdcWallets(accounts);

  const handleAddAccount = () => {
    const trimmedAddress = newAccountInput.trim();
    if (!trimmedAddress) { toast('Please enter an address', 'warning', 3000); return; }
    if (!isValidXdcAddress(trimmedAddress)) { toast('Invalid XDC address format', 'error', 4000); return; }
    if (accounts.includes(trimmedAddress)) { toast('This address is already added', 'warning', 3000); return; }
    setAccounts([...accounts, trimmedAddress]);
    setNewAccountInput('');
    setShowAddAccount(false);
    toast('Address added successfully', 'success', 3000);
  };

  const handleRemoveAccount = (address: string) => {
    if (confirm(`Remove address?`)) {
      setAccounts(accounts.filter(a => a !== address));
      toast('Address removed', 'info', 3000);
    }
  };

  const isValidInput = newAccountInput.trim() && isValidXdcAddress(newAccountInput.trim());
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Icons.BarChart size={16} /> },
    { id: 'transactions', label: 'Transactions', icon: <Icons.ArrowRightLeft size={16} /> },
    { id: 'tokens', label: 'Tokens', icon: <Icons.Coins size={16} /> },
  ];

  const allTransactions = wallets.flatMap(w => w.recentTransactions.map(tx => ({ ...tx, walletAddress: w.address }))).sort((a, b) => b.timestamp - a.timestamp);
  const tokenMap = new Map<string, { address: string; name: string; symbol: string; balance: number; decimals: number }>();
  for (const wallet of wallets) {
    for (const token of wallet.tokens) {
      const existing = tokenMap.get(token.address);
      if (existing) { existing.balance += token.balance; }
      else { tokenMap.set(token.address, { ...token }); }
    }
  }
  const aggregatedTokens = Array.from(tokenMap.values());

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<Icons.Xdc size={36} color={XDC_COLORS.primary} />}
        title="XDC Monitor"
        subtitle={t('xdc.subtitle', 'Monitor your XDC addresses and masternodes')}
        primaryAction={
          showAddAccount
            ? {
                label: 'Cancel',
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddAccount(false),
                variant: 'secondary',
              }
            : {
                label: 'Add Address',
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddAccount(true),
              }
        }
        refreshAction={
          accounts.length > 0
            ? {
                label: 'Refresh',
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={XDC_COLORS}
      />

      {showAddAccount && (
        <NetworkAddressInputForm
          value={newAccountInput}
          onChange={setNewAccountInput}
          onSubmit={handleAddAccount}
          submitLabel="Add"
          placeholder="Enter XDC address (xdc... or 0x...)"
          isValid={newAccountInput.trim() ? Boolean(isValidInput) : null}
          colors={XDC_COLORS}
        />
      )}

      <NetworkStatsBanner xdcPrice={xdcPrice} totalAccounts={accounts.length} isLoading={isLoading} />

      {/* V2.K.9 — XDC supply breakdown (pré-mine + inflation
          Masternodes − burn 20 % fees smart-contract + DPoS 108 nodes
          + XDC 2.0). Fed by CoinGecko, refresh every 5 min. */}
      <XdcSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <XdcHealthMetrics />

      {error && <div style={{ ...s.card, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icons.AlertTriangle size={20} /><span>Error: {(error as Error)?.message}</span></div></div>}

      {accounts.length > 0 ? (
        <>
          <StatsOverview totalBalance={totalBalance} totalBalanceUsd={totalBalanceUsd} totalStaked={totalStaked} totalStakedUsd={totalStakedUsd} totalPendingRewards={totalPendingRewards} totalTokens={totalTokens} masternodeCount={masternodeCount} />
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={XDC_COLORS}
            ariaLabel="XDC monitor tabs"
          />

          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}><Icons.Wallet size={18} /> Accounts ({wallets.length})</h2>
              {isLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /></div> : wallets.map(wallet => <AccountCard key={wallet.address} wallet={wallet} onRemove={() => handleRemoveAccount(wallet.address)} />)}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Recent Transactions</h3>
              <ResponsiveTable
                data={allTransactions.slice(0, 50)}
                keyExtractor={(tx, idx) => `${tx.hash}-${idx}`}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    No transactions found
                  </div>
                }
                columns={[
                  {
                    key: 'date',
                    header: 'Date',
                    isSubtitle: true,
                    render: (tx) => xdcAPI.formatTimestamp(tx.timestamp).toLocaleDateString(),
                  },
                  {
                    key: 'hash',
                    header: 'Hash',
                    isTitle: true,
                    render: (tx) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--muted)' }}>
                        {tx.hash.slice(0, 16)}…
                      </span>
                    ),
                  },
                  {
                    key: 'value',
                    header: 'Value',
                    align: 'right',
                    render: (tx) => `${(Number(tx.value) / 1e18).toLocaleString(undefined, { maximumFractionDigits: 4 })} XDC`,
                  },
                ]}
              />
            </div>
          )}

          {activeTab === 'tokens' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>XRC20 Tokens ({aggregatedTokens.length})</h3>
              {aggregatedTokens.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
                  {aggregatedTokens.map(token => (
                    <div key={token.address} style={{ ...s.card, marginBottom: 0, background: 'rgba(0, 188, 212, 0.08)', border: '1px solid rgba(0, 188, 212, 0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div><div style={{ fontSize: '16px', fontWeight: 600, color: XDC_COLORS.accent }}>{token.symbol}</div><div style={{ fontSize: '12px', color: 'var(--muted)' }}>{token.name}</div></div>
                        <div style={{ textAlign: 'right' }}><div style={{ fontSize: '16px', fontWeight: 600 }}>{token.balance.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ ...s.card, textAlign: 'center', color: 'var(--muted)' }}>No tokens found</div>}
            </div>
          )}
        </>
      ) : <EmptyState />}

      <div style={{ marginTop: '30px', padding: '16px 20px', background: 'rgba(29, 111, 163, 0.08)', borderRadius: '12px', border: '1px solid rgba(29, 111, 163, 0.15)', fontSize: '12px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icons.Info size={14} style={{ color: XDC_COLORS.primary }} /><strong style={{ color: 'var(--text)' }}>About XDC Monitor</strong></div>
        <p style={{ lineHeight: 1.6 }}>This page uses the XDCScan API to fetch account data. No wallet connection required.</p>
      </div>
    </div>
  );
}
